import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { phases, recipes } from "@/data/recipes";
import ImagePlaceholder from "@/components/ImagePlaceholder";
import { ArrowLeft, Clock, Beaker, ChefHat, Target } from "lucide-react";

export function Dashboard() {
  return (
    <div className="max-w-7xl mx-auto px-6 py-16 lg:py-20">
      <div className="flex items-center justify-between flex-wrap gap-4 mb-4">
        <div>
          <div className="text-xs uppercase tracking-wide text-emerald-700 font-semibold mb-2">Dein Programm</div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900">Der 12-Wochen-Ernährungsplan</h1>
        </div>
        <div className="text-sm text-slate-500">Dauerhafter Zugang</div>
      </div>
      <p className="text-slate-600 max-w-2xl mb-14">
        Systematischer Aufbau der Lactose-Toleranz in fünf Phasen - evidenzbasiert nach aktueller Studienlage. Rezepte sind Beispiele; passe die Mengen jederzeit an deine individuelle Verträglichkeit an.
      </p>

      <div className="space-y-10">
        {phases.map((phase, idx) => (
          <div key={phase.id} className="grid lg:grid-cols-12 gap-8 items-start" data-testid={`phase-${phase.id}`}>
            <div className="lg:col-span-5">
              <img
                src={phase.image}
                alt={`Phase ${phase.id} - ${phase.title}`}
                loading="lazy"
                className="w-full aspect-[16/9] object-cover rounded-2xl border border-slate-100 shadow-sm"
              />
            </div>
            <Card className="lg:col-span-7 p-8 rounded-2xl border-slate-100 card-lift">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-700 font-bold flex items-center justify-center">{phase.id}</div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-emerald-700 font-semibold">Phase {phase.id} · {phase.weeks}</div>
                  <h2 className="font-semibold text-slate-900 text-2xl tracking-tight">{phase.title}</h2>
                </div>
              </div>
              <p className="text-slate-600 mt-3 leading-relaxed">{phase.focus}</p>
              <div className="mt-5 inline-flex items-center gap-2 text-sm bg-emerald-50 text-emerald-700 rounded-full px-4 py-1.5 font-medium">
                <Target className="w-4 h-4" /> Ziel: {phase.goal}
              </div>

              <div className="mt-6 space-y-2">
                <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-2">Rezepte für diese Phase</div>
                {phase.recipes.map((rk) => (
                  recipes[rk] && (
                    <Link key={rk} to={`/plan/rezept/${rk}`} className="flex items-center justify-between p-3 rounded-xl bg-slate-50 hover:bg-emerald-50 transition-colors text-sm" data-testid={`recipe-link-${rk}`}>
                      <span className="text-slate-800 font-medium">{recipes[rk].title}</span>
                      <span className="text-xs text-slate-500 flex items-center gap-3">
                        <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {recipes[rk].time}</span>
                        <span className="inline-flex items-center gap-1"><Beaker className="w-3 h-3" /> {recipes[rk].lactose}</span>
                      </span>
                    </Link>
                  )
                ))}
              </div>
            </Card>
          </div>
        ))}
      </div>

      <div className="mt-16 p-8 rounded-3xl bg-gradient-to-br from-emerald-50 via-white to-sky-50 border border-slate-100 text-center">
        <h3 className="text-2xl font-semibold text-slate-900">Möchtest du tiefer eintauchen?</h3>
        <p className="mt-2 text-slate-600 max-w-xl mx-auto">Die kompletten wissenschaftlichen Grundlagen und über 30 referenzierte Studien findest du im Deep-Dive.</p>
        <Link to="/wissenschaft" className="inline-block mt-5">
          <Button variant="outline" className="rounded-full btn-lift">Zur Evidenz-Seite</Button>
        </Link>
      </div>
    </div>
  );
}

export function Recipe() {
  const { slug } = useParams();
  const r = recipes[slug];
  if (!r) return <Navigate to="/plan" replace />;

  return (
    <div className="max-w-4xl mx-auto px-6 py-16">
      <Link to="/plan" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-emerald-700 mb-8" data-testid="recipe-back">
        <ArrowLeft className="w-4 h-4" /> Zum Plan
      </Link>
      <div className="text-xs uppercase tracking-wide text-emerald-700 font-semibold mb-3">Rezept</div>
      <h1 className="text-4xl lg:text-5xl font-bold tracking-tight text-slate-900">{r.title}</h1>
      <div className="flex flex-wrap gap-3 mt-6">
        <span className="inline-flex items-center gap-2 text-sm bg-slate-100 text-slate-700 rounded-full px-4 py-1.5"><Clock className="w-4 h-4" /> {r.time}</span>
        <span className="inline-flex items-center gap-2 text-sm bg-emerald-50 text-emerald-700 rounded-full px-4 py-1.5"><Beaker className="w-4 h-4" /> Lactose: {r.lactose}</span>
      </div>

      {r.image ? (
        <img src={r.image} alt={r.title} loading="lazy" className="w-full aspect-[16/9] object-cover rounded-2xl border border-slate-100 my-10" />
      ) : (
        <ImagePlaceholder label="Rezeptbild" ratio="aspect-[16/9]" className="my-10" />
      )}

      <div className="grid md:grid-cols-5 gap-10">
        <div className="md:col-span-2">
          <h2 className="text-xl font-semibold text-slate-900 mb-4">Zutaten</h2>
          <ul className="space-y-2 text-slate-700">
            {r.ingredients.map((it, i) => (
              <li key={i} className="flex gap-3">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-600 mt-2.5 shrink-0" />
                <span>{it}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="md:col-span-3">
          <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2"><ChefHat className="w-5 h-5 text-emerald-700" /> Zubereitung</h2>
          <ol className="space-y-4">
            {r.steps.map((s, i) => (
              <li key={i} className="flex gap-4">
                <span className="w-8 h-8 rounded-full bg-emerald-600 text-white text-sm font-semibold flex items-center justify-center shrink-0">{i + 1}</span>
                <p className="text-slate-700 leading-relaxed pt-1">{s}</p>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </div>
  );
}
